/*
 * Hive header navigation (sections/hive-header.liquid).
 * - <hive-nav>: the desktop menu. Items with child links open a panel of tiles on hover,
 *   from their caret button, or on the first tap on touch screens (snippets/hive-header-dropdown-menu.liquid).
 * - The search field in the mobile menu opens the header search (snippets/hive-header-drawer.liquid).
 * - "/" opens the header search from anywhere on the page.
 */
class HiveNav extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.wrapper = this.closest('.header-wrapper');
    this.section = this.closest('.section-header');
    this.current = null;
    this.pointer = 'mouse';
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.querySelectorAll('[data-hnav-item]').forEach((item) => {
      const link = item.querySelector('.hnav__link');
      const toggle = item.querySelector('.hnav__toggle');

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        // A short pause so a pointer passing over on its way somewhere else doesn't flash the
        // panel open; none when a panel is already showing, so moving between them is instant.
        this.openTimer = setTimeout(() => this.open(item), this.current ? 0 : 90);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.close(), 180);
      });

      // Touch has no hover: the first tap opens the panel, a second tap follows the link.
      link.addEventListener('click', (event) => {
        if (this.pointer === 'mouse' || this.current === item) return;
        event.preventDefault();
        this.open(item);
      });

      toggle.addEventListener('click', (event) => {
        // With a mouse the pointer has usually opened the panel on its way to the caret, so a
        // mouse click only ever opens it; keyboard (detail 0) and touch toggle it.
        if (this.pointer === 'mouse' && event.detail > 0) return this.open(item);
        this.current === item ? this.close() : this.open(item);
      });
    });

    this.addEventListener('pointerdown', (event) => (this.pointer = event.pointerType), true);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.current) return;
      const toggle = this.current.querySelector('.hnav__toggle');
      this.close();
      toggle.focus();
    });
    this.addEventListener('focusout', (event) => {
      if (this.current && !this.current.contains(event.relatedTarget)) this.close();
    });

    // Panel images are lazy, and a hidden panel never loads them. The first sign of interest in
    // the menu starts them, so the tiles are filled in by the time a panel shows.
    const warm = () => {
      this.querySelectorAll('.hnav__panel img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    };
    this.addEventListener('pointerenter', warm, { once: true });
    this.addEventListener('focusin', warm, { once: true });
  }

  disconnectedCallback() {
    this.close();
  }

  open(item) {
    if (this.current === item) return;
    if (this.current) this.close(true);

    const panel = item.querySelector('.hnav__panel');
    panel.hidden = false;
    /* The panel hangs off the bottom of the header bar, so its own top is
       exactly the room it has left. Measured rather than written down as a
       header height that would quietly go stale the next time the bar changes. */
    panel.style.setProperty('--hnav-panel-max', `${Math.max(0, window.innerHeight - panel.getBoundingClientRect().top)}px`);
    item.querySelector('.hnav__toggle').setAttribute('aria-expanded', 'true');
    item.classList.add('is-open');
    this.current = item;

    this.wrapper?.classList.add('hnav-open');
    // Keeps an "on scroll up" header from sliding away under the panel (see StickyHeader).
    if (this.wrapper) this.wrapper.preventHide = true;
    this.section?.classList.add('menu-open');
    document.addEventListener('pointerdown', this.onOutside);
  }

  // `swapping`: another panel opens right after, so the dim and the header state stay as they are.
  close(swapping = false) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    const item = this.current;
    if (!item) return;

    item.querySelector('.hnav__panel').hidden = true;
    item.querySelector('.hnav__toggle').setAttribute('aria-expanded', 'false');
    item.classList.remove('is-open');
    this.current = null;
    if (swapping) return;

    this.wrapper?.classList.remove('hnav-open');
    if (this.wrapper) this.wrapper.preventHide = false;
    this.section?.classList.remove('menu-open');
    document.removeEventListener('pointerdown', this.onOutside);
  }
}

customElements.define('hive-nav', HiveNav);

(() => {
  const headerSearch = () => document.querySelector('.header__icons .header__search > details > summary');

  // The mobile menu's search field: close the menu, then open the real search with its live results.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-hdrawer-search]');
    const drawer = trigger?.closest('header-drawer');
    const search = headerSearch();
    if (!drawer || !search) return;
    event.preventDefault();
    drawer.closeMenuDrawer(event, drawer.querySelector('summary'));
    search.click();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return;
    const target = event.target;
    if (target.isContentEditable || target.closest?.('input, textarea, select')) return;
    const search = headerSearch();
    if (!search || search.parentElement.open || !search.offsetParent) return;
    if (document.documentElement.classList.contains('hive-overlay-open')) return; // something already covers the page
    event.preventDefault();
    search.click();
  });
})();
